// src/services/authService.ts
import axios from 'axios';
import { API_CONFIG } from '../components/config/api';

interface AuthState {
  isAuthenticated: boolean;
  accessToken: string | null;
  refreshToken: string | null;
  expires: number | null;
  membershipId: string | null;
  destinyMembershipId: string | null;
  membershipType: number | null;
  displayName: string | null;
  isAdmin: boolean;  // Added isAdmin field
}

// Initiale Auth-State
let authState: AuthState = {
  isAuthenticated: false,
  accessToken: null,
  refreshToken: null,
  expires: null,
  membershipId: null,
  destinyMembershipId: null,
  membershipType: null,
  displayName: null,
  isAdmin: false  // Initialize isAdmin as false
};

/**
 * Initialisiert die Authentifizierung beim App-Start
 */
export const initAuth = async (): Promise<boolean> => {
  try {
    const storedAuth = localStorage.getItem('destiny_auth');
    if (storedAuth) {
      authState = JSON.parse(storedAuth);
      
      // Prüfen, ob das Token abgelaufen ist
      if (authState.expires && authState.expires < Date.now()) {
        console.log('Token ist abgelaufen, versuche zu aktualisieren...');
        // Token ist abgelaufen, versuche zu aktualisieren
        if (authState.refreshToken) {
          const refreshSuccess = await refreshAccessToken();
          return refreshSuccess;
        } else {
          // Kein Refresh-Token vorhanden, Auth zurücksetzen
          resetAuthState();
          return false;
        }
      }
      
      return authState.isAuthenticated;
    }
    
    return false;
  } catch (error) {
    console.error('Fehler beim Initialisieren der Auth:', error);
    resetAuthState();
    return false;
  }
};

/**
 * Prüft, ob der Benutzer authentifiziert ist
 */
export const isAuthenticated = (): boolean => {
  try {
    const storedAuth = localStorage.getItem('destiny_auth');
    if (!storedAuth) return false;
    
    const authData = JSON.parse(storedAuth);
    // Check for token expiration
    if (authData.expires && authData.expires < Date.now()) {
      // Token expired - quietly reset auth state
      resetAuthState();
      return false;
    }
    return authData.isAuthenticated;
  } catch (error) {
    // In case of parsing errors, reset auth state
    resetAuthState();
    return false;
  }
};

/**
 * Prüft, ob der Benutzer Administrator ist
 */
export const isAdmin = (): boolean => {
  // Wenn nicht authentifiziert, kann der Benutzer kein Admin sein
  if (!isAuthenticated()) {
    return false;
  }
  
  return authState.isAdmin === true;
};

/**
 * Gibt die Bungie OAuth-Login-URL zurück
 */
export const getLoginUrl = (): string => {
  return `${API_CONFIG.BUNGIE_AUTH_URL}?client_id=${API_CONFIG.BUNGIE_CLIENT_ID}&response_type=code&redirect_uri=${encodeURIComponent(API_CONFIG.REDIRECT_URI)}`;
};

/**
 * Processes the Bungie Auth callback and retrieves tokens
 * @param code The authorization code returned by Bungie
 */
export const handleAuthCallback = async (code: string): Promise<boolean> => {
  try {
    console.log('Processing auth callback with code:', code);
    
    // The code parameter is returned by Bungie after authentication
    // We send this to our API to exchange for an access token
    const response = await axios.post(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.AUTH.CALLBACK}`, { 
      code,
      client_id: API_CONFIG.BUNGIE_CLIENT_ID,
      redirect_uri: API_CONFIG.REDIRECT_URI
    }, {
      // Ensure cookies are sent with the request if needed
      withCredentials: true,
      // Explicitly set headers for CORS
      headers: {
        'Content-Type': 'application/json',
      }
    });
    
    console.log('Auth callback response:', response.data);
    
    if (response.data && response.data.status === 'success' && response.data.access_token) {
      // Log all properties received to help debug
      console.log('Auth callback response properties:', Object.keys(response.data));
      
      // Check for required properties
      if (!response.data.destiny_membership_id || !response.data.membership_type) {
        console.warn('Missing destiny membership information in auth response:', {
          destiny_membership_id: response.data.destiny_membership_id,
          membership_type: response.data.membership_type
        });
        
        // Try to fetch user profile to get missing information
        if (response.data.access_token) {
          try {
            const userProfile = await fetchUserDestinyProfile(response.data.access_token);
            
            if (userProfile && userProfile.destinyMembershipId && userProfile.membershipType) {
              console.log('Retrieved missing membership info from profile:', userProfile);
              
              // Use the information from the profile
              response.data.destiny_membership_id = userProfile.destinyMembershipId;
              response.data.membership_type = userProfile.membershipType;
            }
          } catch (profileError) {
            console.error('Failed to fetch user profile:', profileError);
          }
        }
      }
      
      // Check if user is admin (default to false if not specified)
      const isAdmin = response.data.is_admin === true;
      console.log('User is admin:', isAdmin);
      
      // Update auth state with received tokens
      authState = {
        isAuthenticated: true,
        accessToken: response.data.access_token,
        refreshToken: response.data.refresh_token,
        expires: Date.now() + (response.data.expires_in * 1000),
        membershipId: response.data.membership_id,
        destinyMembershipId: response.data.destiny_membership_id,
        membershipType: response.data.membership_type,
        displayName: response.data.display_name,
        isAdmin: isAdmin  // Store admin status
      };
      
      // Additional debug logs
      console.log('Auth state being stored:', {
        isAuthenticated: authState.isAuthenticated,
        membershipId: authState.membershipId,
        destinyMembershipId: authState.destinyMembershipId,
        membershipType: authState.membershipType,
        displayName: authState.displayName,
        isAdmin: authState.isAdmin,
        tokenExpires: authState.expires ? new Date(authState.expires).toISOString() : 'none'
      });
      
      // Store in localStorage
      localStorage.setItem('destiny_auth', JSON.stringify(authState));
      
      // Trigger an event that components can listen for
      window.dispatchEvent(new Event('auth-state-changed'));
      
      console.log('Authentication successful, token received.');
      return true;
    }
    
    console.error('Authentication failed:', response.data);
    return false;
  } catch (error) {
    console.error('Error processing auth callback:', error);
    return false;
  }
};

/**
 * Fetches the user's Destiny profile to get membership information
 */
async function fetchUserDestinyProfile(accessToken: string): Promise<any> {
  try {
    const response = await axios.get(`${API_CONFIG.BUNGIE_API_BASE_URL}/User/GetMembershipsForCurrentUser/`, {
      headers: {
        'X-API-Key': API_CONFIG.BUNGIE_API_KEY,
        'Authorization': `Bearer ${accessToken}`
      }
    });
    
    if (response.data && response.data.Response) {
      const data = response.data.Response;
      
      // Check for destinyMemberships
      if (data.destinyMemberships && data.destinyMemberships.length > 0) {
        // Get the primary membership or the first one
        const primaryMembership = data.primaryMembershipId 
          ? data.destinyMemberships.find((m: any) => m.membershipId === data.primaryMembershipId)
          : data.destinyMemberships[0];
          
        if (primaryMembership) {
          return {
            destinyMembershipId: primaryMembership.membershipId,
            membershipType: primaryMembership.membershipType,
            displayName: primaryMembership.displayName
          };
        }
      }
    }
    
    throw new Error('Could not find Destiny membership information in profile response');
  } catch (error) {
    console.error('Error fetching user Destiny profile:', error);
    throw error;
  }
}

/**
 * Aktualisiert das Access Token mit dem gespeicherten Refresh Token
 */
export const refreshAccessToken = async (): Promise<boolean> => {
  try {
    if (!authState.refreshToken) {
      console.error('Kein Refresh-Token vorhanden');
      return false;
    }

    const response = await axios.post(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.AUTH.REFRESH_TOKEN}`, {
      refresh_token: authState.refreshToken
    });
    
    if (response.data && response.data.status === 'success' && response.data.access_token) {
      console.log('Auth callback response properties:', Object.keys(response.data));
      console.log('Response includes is_admin property:', response.data.hasOwnProperty('is_admin'));
      console.log('is_admin value from response:', response.data.is_admin);
      // Update auth state
      authState = {
        ...authState,
        isAuthenticated: true,
        accessToken: response.data.access_token,
        refreshToken: response.data.refresh_token || authState.refreshToken,
        expires: Date.now() + (response.data.expires_in * 1000)
      };
      
      // Im localStorage speichern
      localStorage.setItem('destiny_auth', JSON.stringify(authState));
      
      console.log('Token erfolgreich aktualisiert');
      return true;
    }
    
    console.error('Token-Aktualisierung fehlgeschlagen:', response.data);
    return false;
  } catch (error) {
    console.error('Fehler beim Aktualisieren des Access Tokens:', error);
    resetAuthState();
    return false;
  }
};

/**
 * Loggt den Benutzer aus
 */
export const logout = async (): Promise<void> => {
  try {
    console.log('Logging out user...');
    
    // Optional: Server-seitigen Logout durchführen
    if (authState.accessToken) {
      try {
        await axios.post(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.AUTH.LOGOUT}`, {}, {
          headers: {
            'Authorization': `Bearer ${authState.accessToken}`
          }
        });
        console.log('Server-side logout completed');
      } catch (apiError) {
        // Nur loggen, aber nicht den Client-Logout blockieren
        console.warn('Error during server-side logout:', apiError);
      }
    }
  } catch (error) {
    console.error('Error during logout process:', error);
  } finally {
    // Immer den Auth-State zurücksetzen, auch wenn ein Fehler auftritt
    resetAuthState();
    console.log('Auth state reset completed');
    
    // Event auslösen, damit andere Komponenten die Änderung bemerken
    window.dispatchEvent(new Event('auth-state-changed'));
  }
};

/**
 * Gibt das aktuelle Access Token zurück
 */
export const getAccessToken = (): string | null => {
  return authState.accessToken;
};

/**
 * Gibt die Bungie Membership ID zurück
 */
export const getMembershipId = (): string | null => {
  return authState.membershipId;
};

/**
 * Gibt die Destiny Membership ID zurück
 */
export const getDestinyMembershipId = (): string | null => {
  return authState.destinyMembershipId;
};

/**
 * Gibt den Membership-Typ zurück
 */
export const getMembershipType = (): number | null => {
  return authState.membershipType;
};

/**
 * Gibt den Display-Namen zurück
 */
export const getDisplayName = (): string | null => {
  return authState.displayName;
};

/**
 * Setzt den Auth-State zurück
 */
const resetAuthState = (): void => {
  console.log('Resetting auth state...');
  
  // Auth-State zurücksetzen
  authState = {
    isAuthenticated: false,
    accessToken: null,
    refreshToken: null,
    expires: null,
    membershipId: null,
    destinyMembershipId: null,
    membershipType: null,
    displayName: null,
    isAdmin: false
  };
  
  // Aus dem localStorage entfernen
  localStorage.removeItem('destiny_auth');
  
  // Auch alle anderen auth-bezogenen Daten entfernen
  sessionStorage.removeItem('returnPath');
  
  console.log('Auth state reset complete');
};

// Axios-Interceptor einrichten, um das Auth-Token zu allen Anfragen hinzuzufügen
axios.interceptors.request.use(
  (config) => {
    // Füge das access token zu Bungie API-Anfragen hinzu
    if (config.url?.includes('bungie.net')) {
      // Bungie API Key Header
      config.headers['X-API-Key'] = API_CONFIG.BUNGIE_API_KEY;
      
      // Auth Token für authentifizierte Anfragen
      if (authState.accessToken) {
        config.headers.Authorization = `Bearer ${authState.accessToken}`;
      }
    }
    // Füge das token zu unseren eigenen API-Anfragen hinzu
    else if (authState.accessToken) {
      config.headers.Authorization = `Bearer ${authState.accessToken}`;
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Axios-Interceptor für Token-Refresh bei 401
let isRefreshing = false;

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      authState.refreshToken &&
      !isRefreshing
    ) {
      originalRequest._retry = true;
      isRefreshing = true;

      const success = await refreshAccessToken();

      isRefreshing = false;

      if (success && authState.accessToken) {
        originalRequest.headers = {
          ...originalRequest.headers,
          Authorization: `Bearer ${authState.accessToken}`,
        };
        return axios(originalRequest);
      } else {
        console.warn("Token konnte nicht aktualisiert werden – Logging out...");
        await logout();
        return Promise.reject(new Error("Session abgelaufen – neu einloggen"));
      }
    }

    return Promise.reject(error);
  }
);
